import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Tag } from "@amzn/storm-ui";
import React from "react";

import { insightTypeToTagVariant } from "../components/discovery/insights/insightDetails/styles";
import { saveUserPreferences } from "../utils/userPreferences.utils";

import { zustandStore } from "./zustand.helpers";

export enum UserPreference {
    // Storing as a string enum to better support Object.Entries(UserPreferenceData) calls
    USE_FORGE_VISUALIZATION = "USE_FORGE_VISUALIZATION",
}

export interface UserPreferenceMetadata {
    // The human readable title of this preference
    title: string;

    // A helpful description that will be displayed to the user
    description: React.ReactNode;

    // If set, when checking for the given featureflag, this user preference value will be used instead
    featureFlagControl?: string;
}

// While this is fine to store here for P0 of the user preference launch, we will move this to Aquila for P1
// I'm using a string literal for the feature flag control to avoid a lot of complicated circular dependency
// issues that would require a non-trivial amount of refactoring to make this work. Since it's going away in P1,
// i'm going to leave it here, but if it's not gone within like 3 months, feel free to yell at me - jqh
export const UserPreferenceData: Record<
    UserPreference,
    UserPreferenceMetadata
> = {
    [UserPreference.USE_FORGE_VISUALIZATION]: {
        title: "Forge Visualization",
        description: (
            <>
                Enable Forge for insight visualization, for a faster interactive
                discovery of{" "}
                <Tag
                    label={"Standalone Insights"}
                    variant={insightTypeToTagVariant[InsightType.INSIGHT]}
                />{" "}
                in-app, allowing you to translate and explore insight data
                without the need to download it.
            </>
        ),
        featureFlagControl: "forgeViz",
    },
};

export type UserPreferenceList = {
    [flag in UserPreference]: string;
};

export type UserPreferencesState = {
    preferences: UserPreferenceList;
};

export interface UserPreferencesActions {
    /**
     * Retrieves all of the current users cached preferences
     * @returns The current user preference state
     */
    getAllPreferences: () => UserPreferenceList;

    /**
     * Retrieves the value for a single user preference from the cache
     * @param preference The preference we want to retrieve the value for
     */
    getPreference: (preference: UserPreference) => string;

    /**
     * Saves the given preferences to the cache, and async saves them to storage
     * @param preferences The preferences we want to update the values for
     */
    setPreferences: (preferences: Partial<UserPreferenceList>) => void;
}

export const initialState: UserPreferencesState = {
    preferences: {
        [UserPreference.USE_FORGE_VISUALIZATION]: "false",
    },
};

export const { store: UserPreferencesStore } = zustandStore<
    UserPreferencesState,
    UserPreferencesActions
>("FeatureFlags", initialState, (set, get) => ({
    getAllPreferences: () => {
        return get().preferences;
    },

    setPreferences: (newPreferences: Partial<UserPreferenceList>) => {
        set(() => ({
            preferences: {
                ...get().preferences,
                ...newPreferences,
            },
        }));
        void saveUserPreferences();
    },

    getPreference: (preference: UserPreference) => {
        return get().preferences[preference];
    },
}));
